import dayjs, { type Dayjs } from '@/config/dayjs';
import type {
  NetWorthChangeDirection,
  NetWorthChangePeriod,
} from '@/interfaces/netWorthChanges';
import {
  type NetWorthChangeRange,
  clampNetWorthChangePeriod,
  defaultNetWorthChangeRange,
  isNetWorthChangeRange,
  resolveNetWorthChangeRange,
} from './netWorthChangeRanges';

/** User-settings key for the chosen range. The role prefix is added on write. */
export const NET_WORTH_CHANGES_RANGE_KEY = 'NetWorthChangesDateRange';

/** User-settings key for All / Gainers / Losers. */
export const NET_WORTH_CHANGES_VIEW_KEY = 'NetWorthChangesView';

const CUSTOM_PREFIX = 'custom:';
const CUSTOM_SEPARATOR = '|';

/**
 * What the advisor picked: a preset, which re-resolves against today every
 * time it is read, or a custom range, which keeps its dates.
 */
export type NetWorthChangeSelection =
  | { kind: 'preset'; range: NetWorthChangeRange }
  | { kind: 'custom'; period: NetWorthChangePeriod };

/**
 * Stores a preset by name rather than by its dates, so "Last 7 Days" chosen
 * last week still means the last seven days when the advisor comes back.
 */
export function serializeNetWorthChangeSelection(
  selection: NetWorthChangeSelection,
): string {
  if (selection.kind === 'preset') return selection.range;
  const { startDate, endDate } = selection.period;
  return `${CUSTOM_PREFIX}${startDate}${CUSTOM_SEPARATOR}${endDate}`;
}

function parseCustom(value: string): NetWorthChangePeriod | null {
  const [startDate, endDate] = value
    .slice(CUSTOM_PREFIX.length)
    .split(CUSTOM_SEPARATOR);
  if (!startDate || !endDate) return null;
  return { startDate, endDate };
}

/**
 * Reads a stored choice back. Anything unusable -- nothing saved, a value
 * from an older format, a custom range whose dates do not parse or have no
 * span, or a preset with no span today -- falls back to the default rather
 * than to an empty widget.
 */
export function parseNetWorthChangeSelection(
  stored: string | undefined,
  today: Dayjs = dayjs(),
): NetWorthChangeSelection {
  const fallback: NetWorthChangeSelection = {
    kind: 'preset',
    range: defaultNetWorthChangeRange(today),
  };
  if (!stored) return fallback;

  if (stored.startsWith(CUSTOM_PREFIX)) {
    const period = parseCustom(stored);
    if (!period) return fallback;
    if (!clampNetWorthChangePeriod(period, today)) return fallback;
    return { kind: 'custom', period };
  }

  if (!isNetWorthChangeRange(stored)) return fallback;
  if (!resolveNetWorthChangeRange(stored, today)) return fallback;
  return { kind: 'preset', range: stored };
}

/**
 * The dates a selection compares, ready to ask for. Null only for a custom
 * range that has no span once its end is pulled back to yesterday.
 */
export function periodForSelection(
  selection: NetWorthChangeSelection,
  today: Dayjs = dayjs(),
): NetWorthChangePeriod | null {
  if (selection.kind === 'preset') {
    return resolveNetWorthChangeRange(selection.range, today);
  }
  return clampNetWorthChangePeriod(selection.period, today);
}

const DIRECTIONS: readonly NetWorthChangeDirection[] = [
  'all',
  'gainers',
  'losers',
];

/** Reads a stored All / Gainers / Losers choice, defaulting to All. */
export function parseNetWorthChangeDirection(
  stored: string | undefined,
): NetWorthChangeDirection {
  return DIRECTIONS.find((direction) => direction === stored) ?? 'all';
}
